/**
 * CommitGateCard — iter84.57.5 / iter84.57.6
 *
 * Hard gate between Phase 1 (pre-commit safety: Parity Sweep, Deploy
 * Readiness, Values Engine) and Phase 2 (CI / Arm / Deploy). Until
 * all three Phase-1 gates are `complete` or `waived`, the card renders
 * a locked panel that:
 *   - Lists every blocking gate by name with its current state
 *   - Explains the operator cannot Save-to-GitHub yet
 *   - Stays read-only (no clicks possible — the unlock condition is
 *     resolved by completing/waiving the underlying gates above)
 *
 * Once all three are pass/waived AND no SHA has been captured yet, the
 * panel flips to a green "Ready to commit" state with a one-click
 * **Save to GitHub + Capture SHA** flow:
 *   1. Operator clicks the action button
 *   2. We surface Emergent's Save-to-GitHub instruction (one toast),
 *      THEN hit `POST /api/release-ops/promotion/{id}/capture-sha`
 *      which server-side fetches the freshly-pushed top-of-main SHA
 *      from GitHub and stamps it on the promotion
 *   3. Card flips to the SHA-captured terminal state — short SHA
 *      shown inline, operator proceeds to Phase 2
 *
 * Why a server-side SHA fetch instead of an input field?
 * The operator can't reliably copy-paste their just-pushed SHA from
 * Emergent's chat UI (it's not exposed there), and asking them to go
 * to GitHub.com to copy it defeats the one-click ergonomics the gate
 * is trying to provide. Server-side fetch closes the loop: click → pull
 * top SHA → persist → return updated promotion.
 *
 * Why a separate card and not an `InlineGateCard`? Because this isn't
 * a gate the operator approves — it's a *summary* gate computed from
 * three upstream gates. There's no manual approve/waive/deny on this
 * row; the only way to unlock it is to resolve the upstream gates.
 */
import { useEffect, useState, useCallback } from "react";
import { Lock, Unlock, ShieldCheck, AlertTriangle, GitCommit, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Gate IDs that must be pass/waived before commit. Mirrors the four
// cards rendered in Phase 1 of ReleaseOpsTab.
//
// iter84.59.15 — added `auto_verify_preview`: if the Phase 4 Post-Deploy
// Auto-Verify card is RED on preview, the operator should not Save-to-
// GitHub. The verdict is lifted from the Phase 4 card and the parent
// auto-flips this gate (pass → Approved, warn → Waived, fail → stays
// Pending). The CommitGateCard then keeps the lock engaged.
const PHASE_1_GATES = [
    { itemId: "parity_sweep_clear",    label: "Parity Sweep",       hint: "preview ↔ production drift audit" },
    { itemId: "readiness_green",       label: "Deployment Readiness", hint: "env signals + protection categories" },
    { itemId: "values_check_preview",  label: "Values Engine",      hint: "static config audit (templates, scoring, etc.)" },
    // iter87.209 — Vendor Health (1.5) was missing here; now a real blocker.
    { itemId: "vendor_health_green",   label: "Vendor Health",      hint: "renewal radar + Resend domain + API-Sports quota — must not be RED" },
    { itemId: "auto_verify_preview",   label: "Auto-Verify (preview)", hint: "Phase 4 programmatic verifier — must not be RED" },
    // iter86.19.1 — 1.6 Preview Acceptance (added iter86.8) was missing here,
    // so the commit gate unlocked before the operator dispositioned it.
    { itemId: "preview_acceptance",    label: "Preview acceptance", hint: "shipped features meet their acceptance checklist (waivable for fix-only iters)" },
];

// Gate is considered "passed" (unblocking) if it's been actively
// resolved — either auto-flipped on a clean verdict, or waived with a
// reason. "pending" (no check yet) and "denied" both block commit.
const PASS_STATES = new Set(["complete", "waived", "approved"]);

function isPass(check) {
    if (!check) return false;
    return PASS_STATES.has(check.state);
}

function shortSha(sha) {
    if (!sha) return "";
    return String(sha).slice(0, 7);
}

export function CommitGateCard({ promotion, onUpdated }) {
    const [capturing, setCapturing] = useState(false);
    // iter84.59.20 — JIT sentinel state. `null` while loading; otherwise
    // `{ok, blockers}` from /release-ops/commit-precondition. The
    // capture-SHA button is disabled until this returns `ok: true`.
    const [sentinel, setSentinel] = useState(null);
    const [sentinelLoading, setSentinelLoading] = useState(false);

    const refreshSentinel = useCallback(async () => {
        setSentinelLoading(true);
        try {
            const result = await api.releaseOpsCommitPrecondition();
            setSentinel(result);
        } catch (err) {
            // Treat fetch errors as a hard block — we cannot prove safety.
            setSentinel({
                ok: false,
                blockers: [{
                    id: "sentinel_fetch_failed",
                    label: "Sentinel fetch failed",
                    detail: err.response?.data?.detail || err.message || "Could not reach the pre-commit sentinel.",
                }],
                signals: [],
            });
        } finally {
            setSentinelLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!promotion?.id) return;
        refreshSentinel();
        const onFocus = () => refreshSentinel();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [promotion?.id, refreshSentinel]);

    // iter86.82.2 — No more `return null` without a promotion: render the
    // same always-visible tile Phase 2 (ARM + DEPLOY) and Phase 3
    // (FINALIZE) show, with a disabled CTA, so the operator always sees
    // the "Commit to GitHub" step in the ladder.
    const noPromotion = !promotion?.id;

    const states = noPromotion ? [] : PHASE_1_GATES.map((g) => ({
        ...g,
        check: promotion.checks?.[`pre_deploy:${g.itemId}`],
    }));
    const blockers = states.filter((s) => !isPass(s.check));
    const allClear = !noPromotion && blockers.length === 0;
    const capturedSha = promotion?.target_sha || "";
    const isCommitted = Boolean(capturedSha);

    const captureSha = async () => {
        // iter84.59.20 / iter86.38 — Re-check the sentinel right before
        // allowing the SHA capture. If `.env` drifted to a non-preview
        // URL between page load and click, we MUST block: the deploy
        // rewrite would skip it and bake it into the production bundle.
        let snapshot = sentinel;
        try {
            snapshot = await api.releaseOpsCommitPrecondition();
            setSentinel(snapshot);
        } catch (err) {
            const msg = err.response?.data?.detail || "Could not reach the pre-commit sentinel — refusing to capture SHA.";
            toast.error(msg);
            return;
        }
        if (!snapshot?.ok) {
            toast.error("Pre-commit sentinel BLOCKED — fix /app/frontend/.env before clicking Save to GitHub.");
            return;
        }
        setCapturing(true);
        try {
            const updated = await api.runbookCaptureSha(promotion.id);
            toast.success(
                `SHA captured: ${shortSha(updated.target_sha)} — proceed to Phase 2`,
            );
            onUpdated?.(updated);
        } catch (err) {
            const msg = err.response?.data?.detail || "Could not capture SHA from GitHub";
            toast.error(msg);
        } finally {
            setCapturing(false);
        }
    };

    // ---- Card styling per state (mirrors the Phase 2 ARM tile) ----
    const cardCls = isCommitted
        ? "border-emerald-500/60 bg-emerald-500/[0.04]"
        : allClear
            ? "border-cyber/40 bg-cyber/[0.03]"
            : "border-zinc-800";
    const iconCls = isCommitted ? "text-emerald-400" : allClear ? "text-cyber" : "text-zinc-500";
    const headline = noPromotion
        ? "Start a promotion in Phase 0 first"
        : isCommitted
            ? `Committed — SHA ${shortSha(capturedSha)} captured`
            : allClear
                ? "Safety checks clear — Save to GitHub (branch: dev), then capture the SHA"
                : `${blockers.length} of ${PHASE_1_GATES.length} pre-commit checks pending — resolve the Phase 1 gates`;

    return (
        <div
            data-testid="commit-gate-card"
            data-all-clear={allClear ? "1" : "0"}
            data-committed={isCommitted ? "1" : "0"}
            className={`brutal-card p-3 my-3 space-y-3 border-2 ${cardCls}`}
        >
            {/* Always-visible header row: icon · phase label + headline · CTA */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <span className={iconCls}>
                        {isCommitted
                            ? <GitCommit className="w-5 h-5" />
                            : allClear
                                ? <Unlock className="w-5 h-5" />
                                : <Lock className="w-5 h-5" />}
                    </span>
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                            Phase 1 · Commit to GitHub
                        </p>
                        <p className="text-xs text-zinc-200 font-bold" data-testid="commit-gate-headline">
                            {headline}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={captureSha}
                    disabled={noPromotion || !allClear || isCommitted || capturing
                        || !sentinel || !sentinel.ok || sentinelLoading}
                    data-testid="commit-gate-capture-sha-btn"
                    title={
                        noPromotion
                            ? "Start a promotion in Phase 0 first"
                            : isCommitted
                                ? "SHA already captured — use the re-capture link below after a fix commit"
                                : !allClear
                                    ? "All Phase 1 safety checks must be pass or waived first"
                                    : "Save to GitHub in Emergent first, then click to stamp the pushed SHA"
                    }
                    className={`brutal-btn text-xs py-2 px-3 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isCommitted ? "border-emerald-500 text-emerald-300" : allClear ? "border-cyber text-cyber" : ""
                    }`}
                >
                    {capturing ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching SHA…</>
                    ) : isCommitted ? (
                        <><GitCommit className="w-3 h-3" /> Committed ✓</>
                    ) : allClear && (sentinelLoading || !sentinel) ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking sentinel…</>
                    ) : allClear && !sentinel.ok ? (
                        <><Lock className="w-3.5 h-3.5" /> Blocked by sentinel</>
                    ) : (
                        <><GitCommit className="w-3 h-3" /> Saved — Capture SHA</>
                    )}
                </button>
            </div>

            {!noPromotion && (
                <div className="space-y-2 pl-7">
                    {isCommitted ? (
                        <div className="text-xs text-zinc-300 leading-relaxed" data-testid="commit-gate-committed-message">
                            <p>
                                <ShieldCheck className="inline w-3.5 h-3.5 mr-1 text-emerald-400" />
                                Captured top-of-branch SHA: <span
                                    className="font-mono text-emerald-300"
                                    data-testid="commit-gate-captured-sha"
                                    title={capturedSha}
                                >
                                    {shortSha(capturedSha)}
                                </span>
                            </p>
                            {/* iter86.77.12 — Show WHICH commit got stamped so a
                              * stale pre-push head is instantly recognisable. */}
                            {promotion.target_sha_message && (
                                <p className="mt-1 text-[11px] text-zinc-400 font-mono truncate" data-testid="commit-gate-captured-message" title={promotion.target_sha_message}>
                                    “{promotion.target_sha_message}”
                                    {promotion.target_sha_commit_date && (
                                        <span className="text-zinc-600"> · committed {new Date(promotion.target_sha_commit_date).toLocaleString()}</span>
                                    )}
                                </p>
                            )}
                            {promotion.target_sha_capture_warning && (
                                <p className="mt-1.5 text-[11px] text-amber-300/90 border border-amber-500/40 bg-amber-500/10 p-2" data-testid="commit-gate-capture-warning">
                                    <AlertTriangle className="inline w-3 h-3 mr-1" />
                                    {promotion.target_sha_capture_warning}
                                </p>
                            )}
                            <p className="mt-1.5 text-zinc-400">
                                Continue with <b>Phase 2 · CI & Deploy</b> below — the SHA is now pre-filled in the CI step.
                            </p>
                            {/* iter86.34 — Re-capture path: a fix commit pushed after
                              * capture (e.g. CI went red) changes top-of-dev. The
                              * backend endpoint overwrites target_sha any time before
                              * FINALIZE/cancel, so no need to cancel the promotion. */}
                            <button
                                type="button"
                                onClick={captureSha}
                                disabled={capturing}
                                data-testid="commit-gate-recapture-sha-btn"
                                className="brutal-btn-ghost mt-2.5 text-[10px] py-1.5 px-2.5 border border-emerald-500/40 text-emerald-300/90 hover:bg-emerald-500/10 disabled:opacity-50"
                            >
                                {capturing
                                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Re-fetching SHA…</>
                                    : <><GitCommit className="w-3 h-3" /> Pushed a fix commit? Re-capture SHA</>}
                            </button>
                        </div>
                    ) : allClear ? (
                        <div className="space-y-2.5">
                            <p className="text-xs text-zinc-300 leading-relaxed" data-testid="commit-gate-ready-message">
                                <ShieldCheck className="inline w-3.5 h-3.5 mr-1 text-emerald-400" />
                                All Phase 1 safety checks are <b className="text-emerald-400">pass</b> or <b className="text-emerald-400">waived</b>.
                            </p>
                            <ol className="text-[11px] text-zinc-300 list-decimal list-inside space-y-1">
                                <li>Use Emergent's <b>Save to GitHub</b> control in the chat input to push your commit.</li>
                                <li>Then click the button below — we'll fetch the freshly-pushed SHA from GitHub and stamp it on this promotion.</li>
                            </ol>
                            {/* iter84.59.20 — JIT pre-commit sentinel.
                              * Re-runs the env-collapse checks in strict
                              * mode at the exact moment the operator is
                              * about to Save-to-GitHub. The button below
                              * is hard-locked until the sentinel is ok. */}
                            {sentinel && !sentinel.ok && (
                                <div
                                    data-testid="commit-gate-sentinel-blocked"
                                    className="border-2 border-red-500/70 bg-red-500/10 p-2.5 space-y-1.5"
                                >
                                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-red-400">
                                        <AlertTriangle className="w-3.5 h-3.5" />
                                        Sentinel blocked — fix .env before commit
                                    </div>
                                    <ul className="text-[11px] text-red-200 list-disc list-inside space-y-1" data-testid="commit-gate-sentinel-blockers">
                                        {sentinel.blockers.map((b) => (
                                            <li key={b.id} data-testid={`commit-gate-sentinel-row-${b.id}`}>
                                                <b>{b.label}:</b> {b.detail}
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        type="button"
                                        onClick={refreshSentinel}
                                        disabled={sentinelLoading}
                                        data-testid="commit-gate-sentinel-recheck-btn"
                                        className="brutal-btn-sm text-[10px] border-red-500 text-red-200 hover:bg-red-500/20 disabled:opacity-50"
                                    >
                                        {sentinelLoading ? "Rechecking…" : "Recheck"}
                                    </button>
                                </div>
                            )}
                            {sentinel && sentinel.ok && (
                                <div
                                    data-testid="commit-gate-sentinel-ok"
                                    className="text-[11px] text-emerald-400/90 flex items-center gap-1.5"
                                >
                                    <ShieldCheck className="w-3 h-3" />
                                    Pre-commit sentinel clear — .env is deploy-safe (preview URL; the deploy pipeline substitutes the production secret at build).
                                </div>
                            )}
                            {/* iter84.70 — Auto-repair audit strip. When the
                              * sentinel's iter84.60.5.1.6 self-heal restored a
                              * collapsed .env, surface the WARN signal so the
                              * operator has a visible audit trail of what the
                              * system prevented. Non-blocking by design. */}
                            {(() => {
                                const repaired = sentinel?.signals?.find(
                                    (s) => s.id === "env_collapse_auto_repaired" && s.status === "warn",
                                );
                                if (!repaired) return null;
                                return (
                                    <div
                                        data-testid="commit-gate-auto-repair-strip"
                                        className="border-2 border-amber-500/60 bg-amber-500/10 p-2.5 space-y-1"
                                    >
                                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-400">
                                            <AlertTriangle className="w-3 h-3" />
                                            .env auto-repaired — audit trail
                                        </div>
                                        <p className="text-[11px] text-amber-200/90 leading-relaxed" data-testid="commit-gate-auto-repair-detail">
                                            {repaired.detail}
                                        </p>
                                    </div>
                                );
                            })()}
                            {/* CTA moved to the always-visible header row
                                (iter86.82.2) — mirrors the Phase 2 ARM tile. */}
                        </div>
                    ) : (
                        <>
                            <p className="text-xs text-zinc-300 leading-relaxed" data-testid="commit-gate-blocked-message">
                                <AlertTriangle className="inline w-3.5 h-3.5 mr-1 text-amber-400" />
                                Do <b>not</b> Save-to-GitHub yet. Resolve each blocking check in Phase 1 above — once a card emits a clean verdict, its gate auto-flips. Warnings can be waived via the gate's <b>Waive</b> button after entering a reason. The Auto-Verify check reflects the latest Phase 4 verifier result; if it's RED on preview the gate stays Pending and you cannot commit.
                            </p>
                            <ul className="space-y-1 mt-2" data-testid="commit-gate-blocker-list">
                                {states.map((s) => {
                                    const ok = isPass(s.check);
                                    const stateLabel = s.check?.state || "pending";
                                    return (
                                        <li
                                            key={s.itemId}
                                            data-testid={`commit-gate-row-${s.itemId}`}
                                            className={`flex items-center justify-between text-[11px] px-2 py-1.5 border ${
                                                ok ? "border-emerald-700/40 bg-emerald-700/5" : "border-zinc-700 bg-zinc-900/50"
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className={`inline-block w-2 h-2 rounded-full ${
                                                    ok ? "bg-emerald-400" : "bg-amber-400"
                                                }`} />
                                                <span className="font-bold uppercase tracking-wider text-zinc-200">{s.label}</span>
                                                <span className="text-zinc-500">— {s.hint}</span>
                                            </div>
                                            <span className={`font-mono ${ok ? "text-emerald-300" : "text-amber-300"}`}>
                                                {stateLabel}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
